/** Keep in sync with alertops-v2 ui-session-handoff-cookie.util.ts */

export const UI_SESSION_HANDOFF_COOKIE_NAME = 'alertops_ui_session_handoff';
export const UI_SESSION_HANDOFF_MAX_AGE_SEC = 120;

const ESSENTIAL_KEYS = [
  'token',
  'refresh_token',
  'token-key',
  'username',
  'userLocalStorageToken',
  'subdomain',
  'email',
  'identityprovider',
  'currentTheme',
] as const;

export function resolveUiSessionHandoffCookieDomain(): string | null {
  const host = window.location.hostname.toLowerCase();
  if (host === 'alertops.com' || host.endsWith('.alertops.com')) {
    return '.alertops.com';
  }
  return null;
}

export function writeUiSessionHandoffCookie(storage: Record<string, string>): boolean {
  const token = storage.token;
  if (!token) {
    return false;
  }

  const domain = resolveUiSessionHandoffCookieDomain();
  if (!domain) {
    return false;
  }

  try {
    let payloadStorage = storage;
    let encoded = encodeURIComponent(JSON.stringify({ storage: payloadStorage }));
    if (encoded.length > 3500) {
      payloadStorage = slimHandoffStorage(storage);
      encoded = encodeURIComponent(JSON.stringify({ storage: payloadStorage }));
    }
    if (encoded.length > 3500 || !payloadStorage.token) {
      return false;
    }

    document.cookie = [
      `${UI_SESSION_HANDOFF_COOKIE_NAME}=${encoded}`,
      `Max-Age=${UI_SESSION_HANDOFF_MAX_AGE_SEC}`,
      'Path=/',
      'Secure',
      'SameSite=Lax',
      `Domain=${domain}`,
    ].join('; ');
    return true;
  } catch {
    return false;
  }
}

export function consumeUiSessionHandoffCookie(): { storage: Record<string, string> } | null {
  const raw = readUiSessionHandoffCookieValue();
  clearUiSessionHandoffCookie();
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(decodeURIComponent(raw)) as { storage?: Record<string, string> };
    if (!parsed?.storage || typeof parsed.storage !== 'object' || !parsed.storage.token) {
      return null;
    }
    return { storage: parsed.storage };
  } catch {
    return null;
  }
}

export function clearUiSessionHandoffCookie(): void {
  const domain = resolveUiSessionHandoffCookieDomain();
  const expires = 'Thu, 01 Jan 1970 00:00:00 GMT';
  document.cookie = `${UI_SESSION_HANDOFF_COOKIE_NAME}=; Max-Age=0; Path=/; Expires=${expires}`;
  if (domain) {
    document.cookie = `${UI_SESSION_HANDOFF_COOKIE_NAME}=; Max-Age=0; Path=/; Expires=${expires}; Domain=${domain}`;
  }
}

function readUiSessionHandoffCookieValue(): string | null {
  const prefix = `${UI_SESSION_HANDOFF_COOKIE_NAME}=`;
  const parts = document.cookie.split(';');
  for (const part of parts) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      return trimmed.slice(prefix.length);
    }
  }
  return null;
}

function slimHandoffStorage(storage: Record<string, string>): Record<string, string> {
  const slim: Record<string, string> = {};
  for (const key of ESSENTIAL_KEYS) {
    const value = storage[key];
    if (typeof value === 'string' && value) {
      slim[key] = value;
    }
  }
  for (const [key, value] of Object.entries(storage)) {
    if (key.endsWith('-key') && typeof value === 'string' && value) {
      slim[key] = value;
    }
  }
  return slim;
}
